<script lang="ts">
    // Um agendamento dentro da folha (v4): agenda, serviços, sala/insumos, quando, status, repetição,
    // anotações e exclusão. É a mesma lógica do bloco antigo (appointment-form-block.svelte) — muda a
    // apresentação. Quando o cliente tem dois serviços em paralelo, a folha mostra um cartão por
    // agendamento e quem separa os dois é a faixa desenhada pela folha (appointment-sheet.svelte).
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faCalendarDay, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import type { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import ZsheetSection from '../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../_common/z/sheet/zsheet-entity.svelte'
    import ZsheetOptions from '../../_common/z/sheet/zsheet-options.svelte'
    import type { ZsheetOption } from '../../_common/z/sheet/zsheet'
    import ServicesBlock from './blocks/services-block.svelte'
    import WhenBlock from './blocks/when-block.svelte'
    import StatusBlock from './blocks/status-block.svelte'
    import RepeatBlock from './blocks/repeat-block.svelte'
    import ExtrasBlock from './blocks/extras-block.svelte'

    const dispatch = createEventDispatcher()

    export let entity: Appointment
    export let schedules: Schedule[] = []
    export let personId: string = null

    // ---- agenda ----
    let changingSchedule = false
    $: schedule = schedules.find((el) => el.scheduleId === entity?.scheduleId) || null
    $: scheduleOptions = schedules.map(
        (el) =>
            ({
                value: el.scheduleId,
                label: el.label,
                description: openingHours(el),
            }) as ZsheetOption,
    )

    function minuteLabel(minute: number) {
        const h = Math.floor(minute / 60)
        const m = minute % 60
        return m === 0 ? String(h).padStart(2, '0') : String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
    }

    function openingHours(item: Schedule) {
        if (item?.startMinute === undefined || item?.startMinute === null) return ''
        if (item?.endMinute === undefined || item?.endMinute === null) return ''
        return minuteLabel(item.startMinute) + '–' + minuteLabel(item.endMinute) + 'h'
    }

    // trocar de agenda zera os serviços: os atendentes permitidos mudam junto (regra do formulário atual)
    function onScheduleChange(ev) {
        entity.scheduleId = ev.detail
        entity.items = []
        changingSchedule = false
        setState('ready')
        dispatch('change')
    }

    // ---- término automático ----
    function evaluateAuto() {
        if (!entity.auto) return
        let minutes = (entity.items || []).filter((el) => !!el).reduce((acc, el) => acc + (el.service?.duration || 0), 0)
        if (minutes < 10) minutes = 10
        const end = new Date(entity.start)
        end.setMinutes(new Date(entity.start).getMinutes() + minutes)
        entity.end = end
    }

    // ---- sala / equipamento ocupado ----
    let lockedIds: string[] = []
    let requiresResource = false
    let lockedRequestSeq = 0

    async function loadLockedResources() {
        // descarta resposta fora de ordem (horário alterado de novo antes da anterior voltar)
        const seq = ++lockedRequestSeq
        const ids = await window.api.schedulers.resourcesLock.lockedIds(entity.start, entity.end, entity.appointmentId)
        if (seq === lockedRequestSeq) lockedIds = ids
    }

    async function evaluateResources() {
        try {
            requiresResource = (entity.items || []).some((item) => item?.service?.needResource)
            if (requiresResource || entity.resources?.length > 0) await loadLockedResources()
        } catch (error) {
            window.error(error)
        }
    }

    let resourcesBounce = null
    function scheduleResourcesEvaluation() {
        if (resourcesBounce) clearTimeout(resourcesBounce)
        resourcesBounce = setTimeout(() => evaluateResources(), 500)
    }

    // qualquer mudança de horário recarrega os ocupados; o primeiro valor vem do onMount
    let lastRangeKey: string = undefined
    $: onRangeChange(entity?.start, entity?.end)
    function onRangeChange(start: Date, end: Date) {
        const key = start && end ? new Date(start).getTime() + '-' + new Date(end).getTime() : null
        if (key === lastRangeKey) return
        const isFirst = lastRangeKey === undefined
        lastRangeKey = key
        if (!isFirst) scheduleResourcesEvaluation()
    }

    // consumo que veio da receita de um item removido: some (agendamento novo) ou se desvincula (já salvo)
    function cleanupOrphanRecipeConsumption(items) {
        if (!entity.consumption || entity.consumption.length === 0) return
        const current = new Set((items || []).filter((el) => !!el))
        let changed = false
        const kept = []
        for (const consumption of entity.consumption) {
            if (consumption.sourceItem && !current.has(consumption.sourceItem)) {
                changed = true
                if (!entity.appointmentId) continue
                consumption.performedServiceId = undefined
                consumption.sourceItem = undefined
            }
            kept.push(consumption)
        }
        if (changed) entity.consumption = kept
    }
    $: cleanupOrphanRecipeConsumption(entity.items)

    function onItemsChange() {
        evaluateAuto()
        evaluateResources()
        dispatch('change')
    }

    function onWhenChange() {
        evaluateAuto()
        dispatch('change')
    }

    function setState(value: 'ready' | 'typing') {
        dispatch('state', value)
    }

    onMount(async () => {
        await evaluateResources()
    })

    onDestroy(() => {
        if (resourcesBounce) clearTimeout(resourcesBounce)
    })
</script>

<!-- AGENDA -->
<ZsheetSection label="Agenda">
    {#if changingSchedule}
        <div transition:slide={{ duration: 160 }}>
            <ZsheetOptions options={scheduleOptions} value={entity.scheduleId} on:change={onScheduleChange} />
            <button type="button" class="link" on:click={() => (changingSchedule = false)}>Cancelar</button>
        </div>
    {:else}
        <ZsheetEntity
            initials={schedule?.label ? schedule.label.substring(0, 1) : ''}
            icon={faCalendarDay}
            title={schedule?.label || '[agenda não encontrada]'}
            subtitle={openingHours(schedule)}
            clickable={!entity.appointmentId}
            on:click={() => (changingSchedule = !entity.appointmentId)}
        >
            <svelte:fragment slot="trailing">
                {#if !entity.appointmentId}
                    <span class="link">Trocar</span>
                {/if}
            </svelte:fragment>
        </ZsheetEntity>
    {/if}
</ZsheetSection>

<!-- SERVIÇOS -->
<ServicesBlock bind:entity {schedule} {personId} on:change={onItemsChange} on:state={(ev) => setState(ev.detail)} />

<!-- SALA / INSUMOS -->
<ExtrasBlock bind:entity {lockedIds} {requiresResource} on:change={() => dispatch('change')} on:refreshLocked={loadLockedResources} />

<!-- QUANDO -->
<WhenBlock bind:entity on:change={onWhenChange} />

<!-- STATUS -->
<StatusBlock bind:value={entity.status} on:change={() => dispatch('change')} />

<!-- REPETIÇÃO -->
<RepeatBlock
    bind:entity
    on:change={() => dispatch('change')}
    on:editAnother={(ev) => dispatch('editAnother', ev.detail)}
    on:removeAnother={(ev) => dispatch('removeAnother', ev.detail)}
/>

<!-- ANOTAÇÕES -->
<ZsheetSection label="Anotações">
    <textarea class="notes" rows="2" placeholder="Algo que a equipe precisa saber" bind:value={entity.notes} on:change={() => dispatch('change')}></textarea>
</ZsheetSection>

<!-- EXCLUIR -->
{#if window.store.token.permissions.scheduler.scheduler.write}
    <ZsheetSection tone="danger">
        <!-- ícone numa coluna e os dois textos em outra: assim a explicação começa embaixo do título,
             e não embaixo do ícone (que era o que deslocava a linha de baixo) -->
        <button type="button" class="danger" on:click={() => dispatch('remove')}>
            <span class="danger_icon"><Fa icon={faTrashAlt} /></span>
            <span class="danger_text">
                <span class="danger_title">{entity.appointmentId ? 'Excluir agendamento' : 'Descartar este agendamento'}</span>
                <span class="danger_hint">
                    {entity.appointmentId
                        ? 'Apaga o registro definitivamente. Para manter o histórico, use um status de cancelamento.'
                        : 'Remove este bloco da tela.'}
                </span>
            </span>
        </button>
    </ZsheetSection>
{/if}

<style>
    /* valores base = tema escuro */
    .link {
        font-size: 13px;
        opacity: 0.7;
        text-decoration: underline;
        background: none;
        border: 0px;
        color: inherit;
        padding: 6px 0px 0px 0px;
    }
    .notes {
        width: 100%;
        background: #ffffff08;
        border: 1px solid #ffffff1f;
        border-radius: 10px;
        color: inherit;
        font: inherit;
        font-size: 15px;
        padding: 9px 10px;
        resize: vertical;
    }
    .notes:focus {
        outline: none;
        border-color: #2ecc7188;
    }
    .danger {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        width: 100%;
        text-align: left;
        background: none;
        border: 0px;
        color: #e58080;
        font: inherit;
        padding: 0px;
    }
    /* a altura é a da primeira linha do texto: é nela que o ícone se centraliza */
    .danger_icon {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        height: 19px;
        font-size: 14px;
    }
    .danger_text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .danger_title {
        font-size: 14.5px;
        font-weight: 600;
        line-height: 19px;
    }
    .danger_hint {
        font-size: 12.5px;
        opacity: 0.55;
        line-height: 1.3;
        margin-top: 3px;
    }

    :global(.theme_light) .notes {
        background: #ffffff;
        border-color: #d8dbe0;
    }
    :global(.theme_light) .danger {
        color: #c0392b;
    }
</style>
